import { useState, useRef } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { showToast } from "../../features/toast/toastSlice";
import { updateInstitution } from "../../features/counter/counterSlice";
import styles from "./AccessKeyLogin.module.scss";
import { KeyIcon, TickIcon } from "../../components/AccessKeyModal/AccessKeyModal";
import useAppModernisation from "../../hooks/useAppModernisation";

function AccessKeyLogin({ isOpen, onClose }) {
  const [accessKey, setAccessKey] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const inputRef = useRef(null);
  const dispatch = useDispatch();
  const { isAppMode } = useAppModernisation();

  function showToastMessage({
    type = "info",
    primaryMessage,
    secondaryMessage,
    icon,
    autoHideDuration,
  }) {
    const toastPayload = {
      type,
      primaryMessage,
      secondaryMessage,
      ...(icon && { icon }),
    };
    if (autoHideDuration !== undefined) {
      toastPayload.autoHideDuration = autoHideDuration;
    }
    dispatch(showToast(toastPayload));
  }

  function showSuccessToast(primaryMessage, secondaryMessage, icon = TickIcon) {
    showToastMessage({
      type: "success",
      primaryMessage,
      secondaryMessage,
      icon,
    });
  }

  function handleError(primaryMessage, secondaryMessage, icon = KeyIcon) {
    setHasError(true);
    setIsLoading(false);
    setErrorMessage(primaryMessage);
    setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 100);
  }

  function handleInputChange(e) {
    setAccessKey(e.target.value);
    if (hasError) {
      setHasError(false);
      setErrorMessage("");
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!accessKey.trim()) {
      handleError("Access key is required", "Please enter an access key");
      return;
    }
    setIsLoading(true);
    try {
      const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
      if (!base64Regex.test(accessKey)) {
        handleError("Invalid access key format", "Access key must be a valid base64 string");
        return;
      }

      let decodedString, decodedToken;
      try {
        decodedString = atob(accessKey);
        if (!decodedString || decodedString.trim() === "") {
          throw new Error("Empty decoded string");
        }
        decodedToken = JSON.parse(decodedString);
        if (!decodedToken || typeof decodedToken !== "object") {
          throw new Error("Invalid token structure");
        }
      } catch (decodeError) {
        handleError("Invalid access key", "Please check your key and try again");
        return;
      }

      if ("exp" in decodedToken) {
        // handle both seconds and milliseconds timestamp formats
        const expTime =
          decodedToken.exp > 1000000000000 ? decodedToken.exp : decodedToken.exp * 1000;
        if (expTime < Date.now()) {
          handleError("Access key expired", "Please request a new access key");
          return;
        }
      }

      localStorage.setItem("tgt", accessKey);

      dispatch(
        updateInstitution({
          name: decodedToken.name,
          id: decodedToken.id,
          orgid: decodedToken.orgid,
          shortName: decodedToken.shortName,
          instSubscriptionId: decodedToken.instSubscriptionId,
        }),
      );

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "login",
        institution: decodedToken.name,
        origin: "access-key",
      });
      showSuccessToast("Success!", "Access key has been set");
      setAccessKey("");
      setTimeout(() => {
        onClose();
        window.location.reload();
      }, 100);
    } catch (error) {
      handleError("Invalid access key", "Please check your access key and try again");
    } finally {
      setIsLoading(false);
    }
  }

  if (!isOpen) return null;
  return (
    <>
      <div className={styles.overlay}>
        <div className={styles.loginPage}>
          <div className={styles.leftPanel}>
            <img
              src="/TherapeuticGuidelines_Original_R_Logo.svg"
              alt="Therapeutic Guidelines Logo"
              className={styles.logo}
              data-testid="logo-web"
            />
          </div>

          <div className={styles.rightPanel}>
            <div className={isAppMode ? styles.closeIconAppContainer : styles.closeIconContainer}>
              <button
                className={styles.closeButton}
                onClick={onClose}
                aria-label="Close"
                data-testid="close-modal"
              >
                <i className="tg-icons-Cross"></i>
              </button>
            </div>

            <div className={styles.resizeLogoContainer}>
              <img
                src="/TherapeuticGuidelines_Original_R_Logo.svg"
                alt="Therapeutic Guidelines Logo"
                className={styles.resizeLogo}
                data-testid="logo-app"
              />
            </div>
            <div className={`${styles.modal} ${styles.modalSlideIn}`}>
              <div className={styles.header}>
                <h2 className={styles.title}>Log in with Access Key</h2>
                <div className={styles.rightPad} />
              </div>
              <form onSubmit={handleSubmit} className={styles.form}>
                <div className={styles.inputGroup}>
                  <input
                    ref={inputRef}
                    type="text"
                    value={accessKey}
                    onChange={handleInputChange}
                    placeholder="Access key"
                    className={`${styles.input} ${hasError ? styles.error : ""}`}
                    disabled={isLoading}
                    autoFocus
                  />
                  {hasError && errorMessage && (
                    <div className={styles.error} data-testid="accesskey-error-msg">
                      {errorMessage}
                    </div>
                  )}
                </div>
                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={isLoading || !accessKey.trim() || hasError}
                >
                  {isLoading ? "Verifying..." : "Continue"}
                </button>
              </form>
            </div>

            {!isAppMode && (
              <footer className={styles.footer}>
                <div className={styles.footerRow}>
                  <a href="https://tg.org.au/conditions-of-use-and-disclaimer">Conditions of Use</a>
                  <a href="https://tg.org.au/license-agreement">Licence Agreement</a>
                  <a href="https://tg.org.au/privacy-policy">Privacy Policy</a>
                </div>
                <div className={styles.footerRow}>
                  <a href="https://www.tg.org.au/about-us/resources/">Resource Hub</a>
                  <a href="https://tg.org.au/faq">Support and FAQ</a>
                </div>
              </footer>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

AccessKeyLogin.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AccessKeyLogin;
